import React from 'react';
import PropTypes from 'prop-types';
import { Paper, Grid, Chip, Typography, Button, CircularProgress } from '@material-ui/core';
import { showValue } from '../../utils/HelpPreinforme';
import preinformeStyles from './preinformeStyles';

/**
 * Devuelve las iniciales a mostrar en el avatar de la cabecera a partir del
 * nombre mostrable ("--" cuando no hay nombre disponible).
 * @param {string} nombre
 */
export function resolveIniciales(nombre) {
    if (!nombre || nombre === '--') {
        return '--';
    }
    return nombre
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((parte) => parte.charAt(0).toUpperCase())
        .join('');
}

/**
 * Cabecera de solo lectura de la vista de preinforme: identidad del
 * paciente, numeroInforme, fechaMedicion formateada, versionReglas, la
 * rejilla de Documento/Edad/Género/Altura/Estado, y la fila de acciones
 * (Volver / Ver informe PDF / Descargar PDF) alineada a la derecha. Todo
 * Typography y Button: cero input/select/textarea.
 */
const CabeceraPreinforme = ({
    personalData,
    preinforme,
    fechaMedicion,
    onVolver,
    onVerInformePdf,
    isInformeLoad,
    informeUrl,
}) => {
    const classes = preinformeStyles();
    const tieneInforme = showValue(preinforme.numeroInforme) !== '--';

    return (
        <Paper className={classes.headerPaper}>
            <div className={classes.headerTopRow}>
                <div className={classes.headerIdentity}>
                    <div className={classes.headerAvatar}>
                        {resolveIniciales(personalData.nombre)}
                    </div>
                    <div>
                        <Typography variant="h6" className={classes.headerName}>
                            {personalData.nombre}
                        </Typography>
                        <Chip
                            size="small"
                            label={`Informe: ${showValue(preinforme.numeroInforme)}`}
                        />
                    </div>
                </div>
                <div className={classes.headerActionsCol}>
                    <div className={classes.headerActionsRow}>
                        <Button
                            variant="outlined"
                            color="default"
                            size="small"
                            className={classes.buttonNoTransform}
                            onClick={onVolver}
                        >
                            Volver
                        </Button>
                        {tieneInforme && (
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                className={classes.buttonNoTransform}
                                disabled={isInformeLoad}
                                onClick={onVerInformePdf}
                            >
                                Ver informe PDF
                            </Button>
                        )}
                        {isInformeLoad && (
                            <CircularProgress size={20} className={classes.actionsSpinner} />
                        )}
                        {tieneInforme && !isInformeLoad && informeUrl && (
                            <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                className={classes.buttonNoTransform}
                                component="a"
                                href={informeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Descargar PDF
                            </Button>
                        )}
                    </div>
                    <div className={classes.headerRightText}>
                        <Typography variant="body2" className={classes.metaValue}>
                            {fechaMedicion}
                        </Typography>
                        <Typography variant="caption" className={classes.metaLabel} component="span">
                            Reglas {showValue(preinforme.versionReglas)}
                        </Typography>
                    </div>
                </div>
            </div>

            <Grid container spacing={2}>
                <Grid item xs={6} sm={4} md={2}>
                    <Typography variant="caption" className={classes.metaLabel}>
                        Documento
                    </Typography>
                    <Typography className={classes.metaValue}>
                        {showValue(personalData.documento)}
                    </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                    <Typography variant="caption" className={classes.metaLabel}>
                        Edad
                    </Typography>
                    <Typography className={classes.metaValue}>
                        {showValue(preinforme.edad)}
                    </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                    <Typography variant="caption" className={classes.metaLabel}>
                        Género
                    </Typography>
                    <Typography className={classes.metaValue}>
                        {showValue(preinforme.genero)}
                    </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                    <Typography variant="caption" className={classes.metaLabel}>
                        Altura
                    </Typography>
                    <Typography className={classes.metaValue}>
                        {showValue(preinforme.altura)}
                    </Typography>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                    <Typography variant="caption" className={classes.metaLabel}>
                        Estado
                    </Typography>
                    <Typography className={classes.metaValue}>
                        {showValue(preinforme.estado)}
                    </Typography>
                </Grid>
            </Grid>
        </Paper>
    );
};

CabeceraPreinforme.propTypes = {
    personalData: PropTypes.shape({
        nombre: PropTypes.string,
        documento: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }).isRequired,
    preinforme: PropTypes.shape({
        numeroInforme: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        versionReglas: PropTypes.string,
        estado: PropTypes.string,
        edad: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        genero: PropTypes.string,
        altura: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }).isRequired,
    fechaMedicion: PropTypes.string.isRequired,
    onVolver: PropTypes.func.isRequired,
    onVerInformePdf: PropTypes.func.isRequired,
    isInformeLoad: PropTypes.bool,
    informeUrl: PropTypes.string,
};

CabeceraPreinforme.defaultProps = {
    isInformeLoad: false,
    informeUrl: null,
};

export default CabeceraPreinforme;
